import { App } from 'app/console-api';
import { BodyText, Button, FlexColumn, FlexRow } from '@make-software/cspr-design';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import DetailsTableRow from 'app/components/management/common/details-table-row/details-table-row';
import DetailsNameTableData from 'app/components/management/common/details-name-table-data/details-name-table-data';
import DetailsValueTableData from 'app/components/management/common/details-value-table-data/details-value-table-data';
import { InfoAccordion } from 'app/components/management/common/info-accordion/info-accordion';
import { AppIntegrationDetails } from 'app/components/management/apps/common/app-integration-details';
import { StyledCaption } from 'app/components/management/apps/create/apps-create-step-1';
import { AppCategory, getAppCategories } from 'app/console-api/apps/categories';
import { generatePath, useNavigate } from 'react-router-dom';
import { APPS_EDIT_INTEGRATION_PATH } from 'app/route/paths';
import { RewardsType } from 'app/console-api/apps/types';
import { getRewardsTypes } from 'app/console-api/apps/rewards';
import { EditButton, StyledChipsTag } from 'app/components/common/common-styles';

const RawDetailsContainer = styled(FlexRow)(({ theme }) => ({
  padding: '24px 20px'
}));

const StyledUrl = styled(BodyText)(({ theme }) =>
  theme.withMedia({
    ':hover': {
      cursor: 'pointer'
    }
  })
);

const StyledIndicator = styled.div<{ status: string }>(({ theme, status }) => ({
  height: '10px',
  width: '10px',
  borderRadius: '50%',
  backgroundColor:
    status === 'incomplete'
      ? theme.styleguideColors.fillPrimaryRed
      : status === 'complete'
        ? theme.styleguideColors.fillGreen
        : theme.styleguideColors.contentSecondary
}));

const ChipsRow = styled(FlexRow)(({ theme }) =>
  theme.withMedia({
    marginBottom: '24px'
  })
);

const StyledStatusText = styled.div`
  text-transform: capitalize;
`;

export interface AppIntegrationDataProps {
  app: App;
}

export const AppIntegrationData = (props: AppIntegrationDataProps) => {
  const [categories, setCategories] = useState<AppCategory[]>([]);
  const [rewardsTypes, setRewardsTypes] = useState<RewardsType[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    getAppCategories(props.app.id)
      .then((response) => setCategories(response.data || []))
      .catch((error: any) => {
        console.error('Failed to fetch categories', error);
      });

    getRewardsTypes(props.app.id)
      .then((response) => setRewardsTypes(response.data || []))
      .catch((error: any) => {
        console.error('Failed to fetch rewards types', error);
      });
  }, [props.app.id]);

  const handleRedirectUrl = () => {
    window.open(props.app.url, '_blank');
  };

  const edit = () => {
    navigate(generatePath(APPS_EDIT_INTEGRATION_PATH, { appID: props.app.id }));
  };

  return (
    <FlexColumn>
      <StyledCaption margin={'24px 0 32px 17px'}>
        <BodyText size={1} scale={'lg'}>
          Integration
        </BodyText>
      </StyledCaption>
      <DetailsTableRow>
        <DetailsNameTableData> Integration Status: </DetailsNameTableData>
        <DetailsValueTableData>
          <FlexRow align={'center'} gap={4}>
            <StyledIndicator status={props.app.integrationStatus} />
            <StyledStatusText>{props.app.integrationStatus}</StyledStatusText>
          </FlexRow>
        </DetailsValueTableData>
      </DetailsTableRow>
      <DetailsTableRow>
        <DetailsNameTableData>URL</DetailsNameTableData>
        <DetailsValueTableData>
          <StyledUrl onClick={handleRedirectUrl} size={3} scale={'sm'} variation={'blue'}>
            {props.app.url}
          </StyledUrl>
        </DetailsValueTableData>
      </DetailsTableRow>
      <DetailsTableRow>
        <DetailsNameTableData>Claim Notification URL</DetailsNameTableData>
        <DetailsValueTableData> {props.app.claimNotificationUrl} </DetailsValueTableData>
      </DetailsTableRow>
      <DetailsTableRow>
        <DetailsNameTableData> Rewards </DetailsNameTableData>
        <DetailsValueTableData>
          <FlexRow gap={8} align={'center'}>
            {rewardsTypes.map((one) => (
              <StyledChipsTag key={one.type}>
                <BodyText size={3} scale={'xs'}>
                  {one.type} - {one.name}
                </BodyText>
              </StyledChipsTag>
            ))}
          </FlexRow>
        </DetailsValueTableData>
      </DetailsTableRow>
      <DetailsTableRow>
        <DetailsNameTableData> Task categories </DetailsNameTableData>
        <DetailsValueTableData>
          <ChipsRow gap={8} align={'center'} wrap={'wrap'}>
            {categories.map((one) => (
              <StyledChipsTag key={one.name}>
                <BodyText size={3} scale={'xs'}>
                  {one.name}
                </BodyText>
              </StyledChipsTag>
            ))}
          </ChipsRow>
        </DetailsValueTableData>
      </DetailsTableRow>
      <InfoAccordion
        {...props}
        style={{}}
        disableClickAway={true}
        renderContent={() => (
          <RawDetailsContainer>
            <AppIntegrationDetails app={props.app} />
          </RawDetailsContainer>
        )}
      >
        Integration instructions
      </InfoAccordion>
      <EditButton onClick={edit}>Edit</EditButton>
    </FlexColumn>
  );
};
